import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "./MyKeys.css";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Tooltip,
} from "@mui/material";
import { Info, Visibility, VisibilityOff } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteAnthropicKey,
  deleteGeminiKey,
  deleteOpenAIKey,
  deletePineconeKey,
  deleteS3Credentials,
  saveAnthropicKey,
  saveGeminiKey,
  saveOpenAIKey,
  savePineconeKey,
  saveS3Credentials,
} from "../../../../features/organization/orgSlice";
import { accordioStyles } from "./MyKeysStyles";
import theme from "../../../../Constants/theme";
import { inputStyles } from "../../../../Constants/inputStyles";
import {
  deleteButtonStyles,
  iconButtonStyles,
  primaryButtonStyles,
} from "../../../../Constants/buttonStyles";
import { accordionStyles } from "../../../../Constants/accordionStyles";

const MyKeys = () => {
  const [openAIKey, setOpenAIKey] = useState("");
  const [isSavingOpenAI, setIsSavingOpenAI] = useState(false);
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);
  const [openAiFineTuneUsage, setOpenAiFineTuneUsage] = useState();
  const [openAiInferenceUsage, setOpenAiInferencUsage] = useState();
  const [fineTuning, setFineTuning] = useState(false);
  const [inference, setInference] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState("");

  const [pineconeKey, setPineconeKey] = useState("");
  const [pineconeCloud, setPineconeCloud] = useState("");
  const [pineconeRegion, setPineconeRegion] = useState("");
  const [isSavingPinecone, setIsSavingPinecone] = useState(false);
  const [pineconeVerificationMessage, setPineconeVerificationMessage] =
    useState("");
  const [showPineconeKey, setShowPineconeKey] = useState(false);

  const [anthropicKey, setAnthropicKey] = useState("");
  const [anthropicVerificationMessage, setAnthropicVerificationMessage] =
    useState("");
  const [isSavingAnthropic, setIsSavingAnthropic] = useState(false);
  const [showAnthropicKey, setShowAnthropicKey] = useState(false);

  const [geminiKey, setGeminiKey] = useState("");
  const [geminiVerificationMessage, setGeminiVerificationMessage] =
    useState("");
  const [isSavingGemini, setIsSavingGemini] = useState(false);
  const [showGeminiKey, setShowGeminiKey] = useState(false);

  // S3 state hooks
  const [awsAccessKey, setAwsAccessKey] = useState("");
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState("");
  const [s3BucketName, setS3BucketName] = useState("");
  const [s3VerificationMessage, setS3VerificationMessage] = useState("");
  const [showAwsAccessKey, setShowAwsAccessKey] = useState(false);
  const [showAwsSecretKey, setShowAwsSecretKey] = useState(false);
  const [isSavingS3, setIsSavingS3] = useState(false);

  const [messagesSnackbarOpen, setMessagesSnackbarOpen] = useState(false); // For snackbar
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Message for snackbar
  const [snackbarSeverity, setSnackbarSeverity] = useState("info"); // Severity of the alert

  const organization = useSelector((state) => state.organization);
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      organization.organization &&
      organization?.organization?.llm_keys?.openai_key?.key
    ) {
      setOpenAIKey(organization?.organization?.llm_keys?.openai_key?.key);
      setOpenAiFineTuneUsage(
        organization?.organization?.llm_keys?.openai_key?.Finetuning?.limit
      );
      setFineTuning(
        organization?.organization?.llm_keys?.openai_key?.Finetuning?.use
      );

      setOpenAiInferencUsage(
        organization?.organization?.llm_keys?.openai_key?.Inference?.limit
      );
      setInference(
        organization?.organization?.llm_keys?.openai_key?.Inference?.use
      );
    }
    if (
      organization?.organization &&
      organization?.organization?.pinecone_key?.key
    ) {
      setPineconeKey(organization?.organization?.pinecone_key?.key);
      setPineconeCloud(organization?.organization?.pinecone_key?.cloud);
      setPineconeRegion(organization?.organization?.pinecone_key?.region);
    }

    if (organization?.organization?.llm_keys?.anthropic_key?.key) {
      setAnthropicKey(organization?.organization?.llm_keys?.anthropic_key?.key);
    }

    if (organization?.organization?.llm_keys?.gemini_key?.key) {
      setGeminiKey(organization?.organization?.llm_keys?.gemini_key?.key);
    }

    if (
      organization?.organization &&
      organization?.organization?.cloud_keys?.aws?.aws_access_key &&
      organization?.organization?.cloud_keys?.aws?.aws_secret_access_key &&
      organization?.organization?.cloud_keys?.aws?.aws_bucket_name
    ) {
      setAwsAccessKey(
        organization?.organization?.cloud_keys?.aws?.aws_access_key
      );
      setAwsSecretAccessKey(
        organization?.organization?.cloud_keys?.aws?.aws_secret_access_key
      );
      setS3BucketName(
        organization?.organization?.cloud_keys?.aws?.aws_bucket_name
      );
    }
  }, [organization]);

  // save openai
  const handleSaveOpenAI = async () => {
    setIsSavingOpenAI(true);
    try {
      const openAIData = {
        apiKey: openAIKey,
        Finetuning: { use: fineTuning, limit: openAiFineTuneUsage || 0 },
        Inference: { use: inference, limit: openAiInferenceUsage || 0 },
      };
      const openAIResult = await dispatch(
        saveOpenAIKey({
          keyData: openAIData,
          orgId: organization?.organization?._id?.["$oid"],
        })
      ).unwrap();
      if (openAIResult.message === "OpenAI key set successfully") {
        showSnackbar("OpenAI key details saved successfully!", "success");
        setVerificationMessage("OpenAI key details saved successfully!");
      } else {
        showSnackbar(
          "There was an error saving OpenAI key details. Please try again.",
          "error"
        );
      }
    } catch (err) {
      console.error(err);
      showSnackbar(
        "There was an error saving OpenAI key. Please try again.",
        "error"
      );
    } finally {
      setIsSavingOpenAI(false);
    }
  };

  const handleSavePinecone = async () => {
    setIsSavingPinecone(true);
    try {
      const pineconeData = {
        apiKey: pineconeKey,
        cloud: pineconeCloud,
        region: pineconeRegion,
      };
      const pineconeResult = await dispatch(
        savePineconeKey({
          keyData: pineconeData,
          orgId: organization?.organization?._id?.["$oid"],
        })
      ).unwrap();

      if (
        pineconeResult.message === "Pinecone credentials saved successfully"
      ) {
        showSnackbar("Pinecone key details saved successfully!", "success");
        setPineconeVerificationMessage(
          "Pinecone key details saved successfully!"
        );
      } else {
        showSnackbar(
          "There was an error saving Pinecone key details. Please try again.",
          "error"
        );
      }
    } catch (err) {
      console.error(err);
      showSnackbar(
        "There was an error saving Pinecone key. Please try again.",
        "error"
      );
    } finally {
      setIsSavingPinecone(false);
    }
  };

  const handleSaveAnthropic = async () => {
    setIsSavingAnthropic(true);
    try {
      const anthropicData = {
        key: anthropicKey,
        apiKey: anthropicKey,
      };
      const anthropicResult = await dispatch(
        saveAnthropicKey({
          keyData: anthropicData,
          orgId: organization?.organization?._id?.["$oid"],
        })
      ).unwrap();

      if (anthropicResult.message === "anthropic key set successfully") {
        showSnackbar("Anthropic key details saved successfully!", "success");
        setAnthropicVerificationMessage(
          "Anthropic key details saved successfully!"
        );
      } else {
        showSnackbar(
          "There was an error saving Anthropic key details. Please try again.",
          "error"
        );
      }
    } catch (err) {
      console.error(err);
      showSnackbar(
        "There was an error saving Anthropic key. Please try again.",
        "error"
      );
    } finally {
      setIsSavingAnthropic(false);
    }
  };

  const handleSaveGemini = async () => {
    setIsSavingGemini(true);
    try {
      const geminiData = {
        key: geminiKey,
        apiKey: geminiKey,
      };
      const geminiResult = await dispatch(
        saveGeminiKey({
          keyData: geminiData,
          orgId: organization?.organization?._id?.["$oid"],
        })
      ).unwrap();

      if (geminiResult.message === "Gemini key set successfully") {
        setGeminiVerificationMessage("Gemini key details saved successfully!");
        showSnackbar("Gemini key details saved successfully!", "success");
      } else {
        showSnackbar(
          "There was an error saving Gemini key details. Please try again.",
          "error"
        );
      }
    } catch (err) {
      console.error(err);
      showSnackbar(
        "There was an error saving Gemini key. Please try again.",
        "error"
      );
    } finally {
      setIsSavingGemini(false);
    }
  };

  const handleSaveS3 = async () => {
    setIsSavingS3(true);
    try {
      const s3Data = {
        aws_access_key: awsAccessKey,
        aws_secret_access_key: awsSecretAccessKey,
        aws_bucket_name: s3BucketName,
      };
      const s3Result = await dispatch(
        saveS3Credentials({
          keyData: s3Data,
          orgId: organization?.organization?._id?.["$oid"],
        })
      ).unwrap();

      if (s3Result.message === "AWS S3 credentials set successfully") {
        showSnackbar("AWS S3 credentials set successfully.", "success");
        setS3VerificationMessage("AWS S3 credentials set successfully");
      } else {
        showSnackbar("Failed to save S3 credentials.", "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar(
        "There was an error saving Gemini key. Please try again.",
        "error"
      );
    } finally {
      setIsSavingS3(false);
    }
  };

  const handleDeleteOpenAIKey = async () => {
    try {
      // Check if the OpenAI key exists
      if (!openAIKey) {
        showSnackbar("No OpenAI key saved to delete.", "error");
        return; // Exit if no key is available to delete
      }

      // Confirm deletion with the user
      const confirmDelete = window.confirm(
        "Are you sure you want to delete the OpenAI key? This action cannot be undone."
      );
      if (!confirmDelete) return; // If the user cancels, do nothing

      // Dispatch the delete action
      const result = await dispatch(
        deleteOpenAIKey({
          orgId: organization?.organization?._id?.["$oid"],
        })
      ).unwrap();

      // Handle result of deletion
      if (result.message === "OpenAI key deleted successfully") {
        showSnackbar("OpenAI key deleted successfully.", "success");
        setOpenAIKey("");
        setVerificationMessage("");
      } else {
        showSnackbar("There was an error deleting the OpenAI key.", "error");
      }
    } catch (error) {
      console.error("Error deleting OpenAI key:", error);
      showSnackbar(
        "Failed to delete the OpenAI key. Please try again.",
        "error"
      );
    }
  };

  // Function to delete Pinecone key
  const handleDeletePineconeKey = async () => {
    try {
      if (!pineconeKey) {
        showSnackbar("No Pinecone key saved to delete.", "error");
        return;
      }

      const confirmDelete = window.confirm(
        "Are you sure you want to delete the Pinecone key? This action cannot be undone."
      );
      if (!confirmDelete) return;

      const result = await dispatch(
        deletePineconeKey({
          orgId: organization?.organization?._id?.["$oid"],
        })
      ).unwrap();

      if (result.message === "Pinecone key deleted successfully") {
        showSnackbar("Pinecone key deleted successfully.", "success");
        setPineconeKey("");
        setPineconeCloud("");
        setPineconeRegion("");
        setPineconeVerificationMessage("");
      } else {
        showSnackbar("There was an error deleting the Pinecone key.", "error");
      }
    } catch (error) {
      console.error("Error deleting Pinecone key:", error);
      showSnackbar(
        "Failed to delete the Pinecone key. Please try again.",
        "error"
      );
    }
  };

  const handleDeleteAnthropicKey = async () => {
    try {
      if (!anthropicKey) {
        showSnackbar("No Anthropic key saved to delete.", "error");
        return;
      }

      const confirmDelete = window.confirm(
        "Are you sure you want to delete the Anthropic key? This action cannot be undone."
      );
      if (!confirmDelete) return;

      const result = await dispatch(
        deleteAnthropicKey({
          orgId: organization?.organization?._id?.["$oid"],
        })
      ).unwrap();

      if (result.message === "Anthropic key deleted successfully") {
        showSnackbar("Anthropic key deleted successfully.", "success");
        setAnthropicKey("");
        setAnthropicVerificationMessage("");
      } else {
        showSnackbar("There was an error deleting the Anthropic key.", "error");
      }
    } catch (error) {
      console.error("Error deleting Anthropic key:", error);
      showSnackbar(
        "Failed to delete the Anthropic key. Please try again.",
        "error"
      );
    }
  };

  const handleDeleteGeminiKey = async () => {
    try {
      if (!geminiKey) {
        showSnackbar("No Gemini key saved to delete.", "error");
        return;
      }

      const confirmDelete = window.confirm(
        "Are you sure you want to delete the Gemini key? This action cannot be undone."
      );
      if (!confirmDelete) return;

      const result = await dispatch(
        deleteGeminiKey({
          orgId: organization?.organization?._id?.["$oid"],
        })
      ).unwrap();

      if (result.message === "Gemini key deleted successfully") {
        showSnackbar("Gemini key deleted successfully.", "success");
        setGeminiKey("");
        setGeminiVerificationMessage("");
      } else {
        showSnackbar("There was an error deleting the Gemini key.", "error");
      }
    } catch (error) {
      console.error("Error deleting Gemini key:", error);
      showSnackbar(
        "Failed to delete the Gemini key. Please try again.",
        "error"
      );
    }
  };

  const handleDeleteS3Credentials = async () => {
    if (!awsAccessKey && !awsSecretAccessKey && !s3BucketName) {
      showSnackbar("No S3 credentials to delete.", "error");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete the S3 credentials? This action cannot be undone."
    );
    if (!confirmDelete) return;

    try {
      const res = await dispatch(
        deleteS3Credentials({
          orgId: organization?.organization?._id?.["$oid"],
        })
      ).unwrap();

      if (res.message === "AWS S3 credentials deleted successfully") {
        setAwsAccessKey("");
        setAwsSecretAccessKey("");
        setS3BucketName("");
        setS3VerificationMessage("");
        showSnackbar("S3 credentials deleted successfully.", "success");
      } else {
        showSnackbar("Failed to delete S3 credentials.", "error");
      }
    } catch (error) {
      console.error("Error deleting S3 credentials:", error);
      showSnackbar("An error occurred while deleting S3 credentials.", "error");
    }
  };

  // Open snackbar with a message and severity
  const showSnackbar = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setMessagesSnackbarOpen(true);
  };

  // Close snackbar
  const handleSnackbarClose = () => {
    setMessagesSnackbarOpen(false);
  };

  // function to toggle Pinecone key visibility
  const toggleShowPineconeKey = () => {
    setShowPineconeKey((prev) => !prev);
  };

  const cloudOptions = [
    { value: "aws", label: "AWS" },
    { value: "gcp", label: "GCP" },
    { value: "azure", label: "Azure" },
  ];

  const regionOptions = {
    aws: [
      { value: "us-east-1", label: "us-east-1 (Virginia)" },
      { value: "us-west-2", label: "us-west-2 (Oregon)" },
      { value: "eu-west-1", label: "eu-west-1 (Ireland)" },
    ],
    gcp: [
      { value: "us-central1", label: "us-central1 (Iowa)" },
      { value: "europe-west4", label: "europe-west4 (Netherlands)" },
    ],
    azure: [{ value: "eastus2", label: "eastus2 (Virginia)" }],
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Snackbar
        open={messagesSnackbarOpen}
        autoHideDuration={5000} // Closes after 5 seconds
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }} // Position of the snackbar
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Container
        sx={{
          py: 4,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          backgroundColor: theme.main.backgroundBlue,
          width: "100%",
          padding: "20px",
          mt: 4,
          borderRadius: "10px",
        }}
      >
        <Box sx={{ width: "100%" }}>
          {/* API Keys Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              alignItems: "flex-start",
              justifyContent: "space-between",
              width: "100%",
              pb: 4,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{ color: theme.textColors.dark.primary, mb: 2 }}
              >
                Add API Keys
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.textColors.dark.secondary }}
              >
                Note: If you sign up for pro, you do not need to add OpenAI and
                Pinecone keys. Your credits will be managed by Queryloop.
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ borderColor: theme.border.divider, mb: 4 }} />

          {/* OpenAI Key Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              alignItems: "flex-start",
              justifyContent: "space-between",
              width: "100%",
              pb: 3,
            }}
          >
            {/* Left - OpenAI Label and Description */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{ color: theme.textColors.dark.primary, mb: 1 }}
              >
                OpenAI Key
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.textColors.dark.secondary }}
              >
                Add your OpenAI API key to enable LLM capabilities such as
                completion, embedding, and fine-tuning features.
              </Typography>
            </Box>

            {/* Right - Input & Actions */}
            <Box sx={{ flex: 1.5, width: "100%" }}>
              <TextField
                fullWidth
                placeholder="Enter OpenAI Key"
                variant="outlined"
                type={showOpenAIKey ? "text" : "password"}
                value={openAIKey}
                onChange={(e) => {
                  setOpenAIKey(e.target.value);
                  setVerificationMessage("");
                }}
                sx={{ ...inputStyles.textField, mb: 0 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowOpenAIKey((prev) => !prev)}
                        sx={iconButtonStyles}
                      >
                        {showOpenAIKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {verificationMessage && (
                <Typography
                  variant="caption"
                  sx={{
                    my: 1,
                    color: organization?.organization?.llm_keys?.openai_key?.key
                      ? theme.state.success.primary
                      : theme.state.error.primary,
                  }}
                >
                  {verificationMessage}
                </Typography>
              )}

              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <Button
                  onClick={handleSaveOpenAI}
                  disabled={!openAIKey.trim() || isSavingOpenAI}
                  sx={primaryButtonStyles}
                >
                  {isSavingOpenAI ? (
                    <CircularProgress sx={{ color: "white" }} size={14} />
                  ) : (
                    "Save"
                  )}
                </Button>

                <Tooltip
                  title={
                    openAIKey?.trim() === ""
                      ? "Enter OpenAI key first"
                      : !organization?.organization?.llm_keys?.openai_key
                      ? "OpenAI key is not saved." // Show this when the key is empty
                      : "Delete OpenAI key?"
                  }
                >
                  <div>
                    <Button
                      onClick={handleDeleteOpenAIKey}
                      disabled={
                        openAIKey === "" ||
                        !organization?.organization?.llm_keys?.openai_key
                          ?.key ||
                        isSavingOpenAI
                      }
                      sx={deleteButtonStyles}
                    >
                      Delete
                    </Button>
                  </div>
                </Tooltip>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  mt: 2,
                }}
              >
                <Tooltip
                  // title={
                  //   !fineTuning ? "Enable Fine Tuning?" : "Fine Tuning Enabled"
                  // }
                  title="Enterprise Feature"
                >
                  <div>
                    <Switch
                      checked={fineTuning}
                      disabled
                      onChange={(e) => setFineTuning(e.target.checked)}
                      sx={{
                        ml: 0,
                        "& .MuiSwitch-track": {
                          backgroundColor: theme.main.secondaryBlue,
                          opacity: 0.7,
                        },
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: theme.main.primaryBlue,
                        },
                        "& .MuiSwitch-track": {
                          backgroundColor: theme.main.primaryBlue,
                        },
                      }}
                    />
                  </div>
                </Tooltip>
                <Typography
                  sx={{
                    fontSize: theme.typography.fontSize.xs,
                    color: theme.textColors.dark.primary,
                  }}
                >
                  Use for OpenAI Fine Tuning
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: theme.border.divider, mb: 4 }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              alignItems: "flex-start",
              justifyContent: "space-between",
              width: "100%",
              pb: 3,
            }}
          >
            {/* Left - Label & Description */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{ color: theme.textColors.dark.primary, mb: 1 }}
              >
                Anthropic Key
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.textColors.dark.secondary }}
              >
                Enter your Anthropic API key to enable Claude-powered
                capabilities.
              </Typography>
            </Box>

            {/* Right - Input & Buttons */}
            <Box sx={{ flex: 1.5, width: "100%" }}>
              <TextField
                fullWidth
                placeholder="Enter Anthropic API Key"
                variant="outlined"
                type={showAnthropicKey ? "text" : "password"}
                value={anthropicKey}
                onChange={(e) => {
                  setAnthropicKey(e.target.value);
                  setAnthropicVerificationMessage("");
                }}
                disabled={isSavingAnthropic}
                sx={{ ...inputStyles.textField, mb: 0 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowAnthropicKey((prev) => !prev)}
                        sx={iconButtonStyles}
                      >
                        {showAnthropicKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {anthropicVerificationMessage && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 1,
                    color: organization?.organization?.llm_keys?.anthropic_key
                      ?.key
                      ? theme.state.success.primary
                      : theme.state.error.primary,
                  }}
                >
                  {anthropicVerificationMessage}
                </Typography>
              )}

              <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                <Button
                  onClick={handleSaveAnthropic}
                  disabled={anthropicKey.trim() === "" || isSavingAnthropic}
                  sx={primaryButtonStyles}
                >
                  {isSavingAnthropic ? (
                    <CircularProgress size={14} sx={{ color: "white" }} />
                  ) : (
                    "Save"
                  )}
                </Button>
                <Tooltip
                  title={
                    anthropicKey?.trim() === ""
                      ? "Enter Anthropic key first"
                      : !organization?.organization?.llm_keys?.anthropic_key
                          ?.key
                      ? "Anthropic key is not saved."
                      : "Delete Anthropic key?"
                  }
                >
                  <div>
                    <Button
                      onClick={handleDeleteAnthropicKey}
                      disabled={
                        anthropicKey.trim() === "" ||
                        !organization?.organization?.llm_keys?.anthropic_key
                          ?.key ||
                        isSavingAnthropic
                      }
                      sx={deleteButtonStyles}
                    >
                      Delete
                    </Button>
                  </div>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: theme.border.divider, my: 4 }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              alignItems: "flex-start",
              justifyContent: "space-between",
              width: "100%",
              pb: 3,
            }}
          >
            {/* Left - Label & Description */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{ color: theme.textColors.dark.primary, mb: 1 }}
              >
                Gemini Key
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.textColors.dark.secondary }}
              >
                Add your Gemini API key to access Google’s Gemini models. Verify
                before saving.
              </Typography>
            </Box>

            {/* Right - Input & Buttons */}
            <Box sx={{ flex: 1.5, width: "100%" }}>
              <TextField
                fullWidth
                placeholder="Enter Gemini API Key"
                variant="outlined"
                type={showGeminiKey ? "text" : "password"}
                value={geminiKey}
                disabled={isSavingGemini}
                onChange={(e) => {
                  setGeminiKey(e.target.value);
                  setGeminiVerificationMessage("");
                }}
                sx={inputStyles.textField}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowGeminiKey((prev) => !prev)}
                        sx={iconButtonStyles}
                      >
                        {showGeminiKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {geminiVerificationMessage && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 1,
                    color: organization?.organization?.llm_keys?.gemini_key?.key
                      ? theme.state.success.primary
                      : theme.state.error.primary,
                  }}
                >
                  {geminiVerificationMessage}
                </Typography>
              )}

              <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                <Button
                  onClick={handleSaveGemini}
                  disabled={geminiKey === "" || isSavingGemini}
                  sx={primaryButtonStyles}
                >
                  {isSavingGemini ? (
                    <CircularProgress size={14} sx={{ color: "white" }} />
                  ) : (
                    "Save"
                  )}
                </Button>

                <Tooltip
                  title={
                    geminiKey?.trim() === ""
                      ? "Enter Gemini key first"
                      : !organization?.organization?.llm_keys?.gemini_key?.key
                      ? "Gemini key is not saved."
                      : "Delete Gemini key?"
                  }
                >
                  <div>
                    <Button
                      onClick={handleDeleteGeminiKey}
                      disabled={
                        geminiKey === "" ||
                        !organization?.organization?.llm_keys?.gemini_key
                          ?.key ||
                        isSavingGemini
                      }
                      sx={deleteButtonStyles}
                    >
                      Delete
                    </Button>
                  </div>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: theme.border.divider, my: 4 }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              alignItems: "flex-start",
              justifyContent: "space-between",
              width: "100%",
              pb: 3,
            }}
          >
            {/* Left - Label & Description */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{ color: theme.textColors.dark.primary, mb: 1 }}
              >
                Pinecone Key
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.textColors.dark.secondary }}
              >
                Provide your Pinecone API key along with the cloud and region
                details. Required for vector DB support.
              </Typography>
            </Box>

            {/* Right - Inputs & Buttons */}
            <Box sx={{ flex: 1.5, width: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Pinecone API Key */}
                <TextField
                  placeholder="Enter Pinecone API Key"
                  fullWidth
                  variant="outlined"
                  type={showPineconeKey ? "text" : "password"}
                  value={pineconeKey}
                  onChange={(e) => {
                    setPineconeKey(e.target.value);
                    setPineconeVerificationMessage("");
                  }}
                  disabled={isSavingPinecone}
                  sx={{ ...inputStyles.textField, mb: 0 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle visibility"
                          onClick={toggleShowPineconeKey}
                          edge="end"
                          sx={iconButtonStyles}
                        >
                          {showPineconeKey ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        <Tooltip
                          title={
                            <ol style={{ paddingLeft: 14 }}>
                              <li>Log in to Pinecone at pinecone.io.</li>
                              <li>Go to API Keys in the dashboard.</li>
                              <li>Create a new key if needed.</li>
                              <li>Copy the API key.</li>
                            </ol>
                          }
                        >
                          <IconButton>
                            <Info
                              fontSize="small"
                              sx={{ color: iconButtonStyles.color, ml: 0.5 }}
                            />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Cloud and Region Select */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControl fullWidth variant="outlined">
                    <Select
                      value={pineconeCloud}
                      onChange={(e) => {
                        setPineconeCloud(e.target.value);
                        setPineconeRegion("");
                      }}
                      displayEmpty
                      sx={inputStyles.select}
                      endAdornment={
                        <InputAdornment position="end" sx={{ marginRight: 1 }}>
                          <Tooltip
                            title={
                              <ol style={{ paddingLeft: 14 }}>
                                <li>
                                  Navigate to the Database using the side panel
                                  on the left in the Pinecone console.
                                </li>
                                <li>
                                  Click on the <strong>Create Index</strong>{" "}
                                  button.
                                </li>
                                <li>
                                  Scroll to the sections labeled{" "}
                                  <strong>Cloud Provider</strong> and{" "}
                                  <strong>Regions</strong>.
                                </li>
                                <li>
                                  Note your preferred Cloud Provider and Region.
                                </li>
                                <li>
                                  Select your preferred Cloud Provider from the
                                  dropdown menu.
                                </li>
                              </ol>
                            }
                          >
                            <IconButton>
                              <Info fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      }
                    >
                      <MenuItem disabled value="">
                        Select Cloud
                      </MenuItem>
                      {cloudOptions.map((cloud) => (
                        <MenuItem key={cloud.value} value={cloud.value}>
                          {cloud.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    fullWidth
                    variant="outlined"
                    disabled={!pineconeCloud || isSavingPinecone}
                  >
                    <Select
                      value={pineconeRegion}
                      onChange={(e) => {
                        setPineconeRegion(e.target.value);
                      }}
                      displayEmpty
                      sx={inputStyles.select}
                      endAdornment={
                        <InputAdornment position="end" sx={{ marginRight: 1 }}>
                          <Tooltip
                            title={
                              !pineconeCloud ? (
                                "Select Cloud First."
                              ) : (
                                <>
                                  <ol style={{ paddingLeft: 14 }}>
                                    <li>
                                      Select the region where your vector
                                      database will be hosted.
                                    </li>
                                    <li>
                                      Region options are specific to your
                                      selected cloud provider.
                                    </li>
                                  </ol>
                                  <div style={{ marginTop: "8px" }}>
                                    <strong>
                                      Reminder: Only AWS (us-east-1) is
                                      available in the Starter plan.
                                    </strong>
                                  </div>
                                </>
                              )
                            }
                          >
                            <IconButton>
                              <Info fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      }
                    >
                      <MenuItem disabled value="">
                        Select Region
                      </MenuItem>
                      {(regionOptions[pineconeCloud] || []).map((region) => (
                        <MenuItem key={region.value} value={region.value}>
                          {region.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {pineconeVerificationMessage && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 1,
                    color: organization?.organization?.pinecone_key
                      ? theme.state.success.primary
                      : theme.state.error.primary,
                  }}
                >
                  {pineconeVerificationMessage}
                </Typography>
              )}

              <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                <Button
                  onClick={handleSavePinecone}
                  disabled={
                    !pineconeKey ||
                    !pineconeCloud ||
                    !pineconeRegion ||
                    isSavingPinecone
                  }
                  sx={primaryButtonStyles}
                >
                  {isSavingPinecone ? (
                    <CircularProgress size={14} sx={{ color: "white" }} />
                  ) : (
                    "Save"
                  )}
                </Button>
                <Tooltip
                  title={
                    pineconeKey?.trim() === ""
                      ? "Enter Pinecone key first"
                      : !organization?.organization?.pinecone_key
                      ? "Pinecone key is not saved." // Show this when the key is empty
                      : "Delete Pinecone key?"
                  }
                >
                  <div>
                    <Button
                      onClick={handleDeletePineconeKey}
                      disabled={
                        !pineconeKey ||
                        !organization?.organization?.pinecone_key?.key
                      }
                      sx={deleteButtonStyles}
                    >
                      Delete
                    </Button>
                  </div>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: theme.border.divider, my: 4 }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              alignItems: "flex-start",
              justifyContent: "space-between",
              width: "100%",
              pb: 3,
            }}
          >
            {/* Left - Label & Description */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{ color: theme.textColors.dark.primary, mb: 1 }}
              >
                S3 Bucket Credentials
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.textColors.dark.secondary }}
              >
                Provide your S3 Bucket Name and Access key along with the AWS
                Secret Access Key to save data in your S3 buckets.
              </Typography>
            </Box>

            {/* Right - Inputs & Buttons */}
            <Box sx={{ flex: 1.5, width: "100%" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    placeholder="Enter S3 Bucket Name"
                    fullWidth
                    variant="outlined"
                    value={s3BucketName}
                    onChange={(e) => {
                      setS3BucketName(e.target.value);
                      setS3VerificationMessage("");
                    }}
                    sx={{ ...inputStyles.textField, mb: 0 }}
                  />
                  <TextField
                    placeholder="Enter AWS Access Key"
                    fullWidth
                    variant="outlined"
                    type={showAwsAccessKey ? "text" : "password"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle AWS Access Key visibility"
                            onClick={() => setShowAwsAccessKey((prev) => !prev)}
                            edge="end"
                            sx={iconButtonStyles}
                          >
                            {showAwsAccessKey ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    value={awsAccessKey}
                    onChange={(e) => {
                      setAwsAccessKey(e.target.value);
                      setS3VerificationMessage("");
                    }}
                    sx={{ ...inputStyles.textField, mb: 0 }}
                  />
                </Box>

                <TextField
                  placeholder="AWS Secret Access Key"
                  value={awsSecretAccessKey}
                  onChange={(e) => {
                    setAwsSecretAccessKey(e.target.value);
                    setS3VerificationMessage("");
                  }}
                  fullWidth
                  variant="outlined"
                  sx={{ ...inputStyles.textField, mb: 0 }}
                  type={showAwsSecretKey ? "text" : "password"} // Toggle visibility
                  disabled={isSavingS3}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle AWS Secret Access Key visibility"
                          onClick={() => setShowAwsSecretKey((prev) => !prev)}
                          edge="end"
                          sx={iconButtonStyles}
                        >
                          {showAwsSecretKey ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {s3VerificationMessage && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 1,
                    color: organization?.organization?.cloud_keys?.aws
                      ? theme.state.success.primary
                      : theme.state.error.primary,
                  }}
                >
                  {s3VerificationMessage}
                </Typography>
              )}

              <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                <Button
                  disabled={
                    isSavingS3 || // Verifying in progress
                    !awsAccessKey ||
                    !awsSecretAccessKey ||
                    !s3BucketName
                  }
                  onClick={handleSaveS3}
                  sx={primaryButtonStyles}
                >
                  {isSavingS3 ? (
                    <CircularProgress size={14} sx={{ color: "white" }} />
                  ) : (
                    "Save"
                  )}
                </Button>
                <Tooltip
                  title={
                    !awsAccessKey && !awsSecretAccessKey && !s3BucketName
                      ? "No S3 credentials saved to delete"
                      : "Delete S3 credentials?"
                  }
                >
                  <div>
                    <Button
                      disabled={
                        !awsAccessKey && !awsSecretAccessKey && !s3BucketName
                      }
                      onClick={handleDeleteS3Credentials}
                      sx={deleteButtonStyles}
                    >
                      Delete
                    </Button>
                  </div>
                </Tooltip>
              </Box>
            </Box>
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: "white",
              mt: 2,
              mb: 1,
              width: "100%",
              textAlign: "center",
            }}
          >
            FAQs
          </Typography>
          <Box sx={{ width: "100%" }}>
            <Accordion sx={accordionStyles.accordionRoot}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon color="white" />}
                sx={accordionStyles.accordionSummary}
              >
                <Typography>With Queryloop Keys using Pro Package</Typography>
              </AccordionSummary>
              <AccordionDetails sx={accordionStyles.accordionDetails}>
                <ul>
                  <li>No limitation on usage/upload</li>
                  <li>Test and Deploy your LLM Applications on our cloud</li>
                  <li>No limitation on file upload and Ideal responses</li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion sx={accordionStyles.accordionRoot}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon color="white" />}
                sx={accordionStyles.accordionSummary}
              >
                <Typography>How to Get Your OpenAI Key</Typography>
              </AccordionSummary>
              <AccordionDetails sx={accordionStyles.accordionDetails}>
                <ol style={{ paddingLeft: 14 }}>
                  <li>Log in to your OpenAI account at platform.openai.com.</li>
                  <li>
                    Go to the <strong>API Keys</strong> section in your account
                    settings.
                  </li>
                  <li>
                    Create a new API key if needed, or copy an existing key.
                  </li>
                  <li>Note down the key for later use.</li>
                  <li>Paste the API key into the input field above.</li>
                </ol>
                <div style={{ marginTop: "8px" }}>
                  <strong>
                    Reminder: Keep your API key secure and do not share it
                    publicly.
                  </strong>
                </div>
              </AccordionDetails>
            </Accordion>

            <Accordion sx={accordionStyles.accordionRoot}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon color="white" />}
                sx={accordionStyles.accordionSummary}
              >
                <Typography>How to Get Your Pinecone Key</Typography>
              </AccordionSummary>
              <AccordionDetails sx={accordionStyles.accordionDetails}>
                <ol style={{ paddingLeft: 14 }}>
                  <li>
                    Log in to Pinecone at pinecone.io and go to API Keys in the
                    dashboard.
                  </li>
                  <li>Create or copy an API key.</li>
                  <li>
                    Go to the Database section and click{" "}
                    <strong>Create Index</strong>.
                  </li>
                  <li>
                    Note your preferred <strong>Cloud Provider</strong> and{" "}
                    <strong>Region</strong>.
                  </li>
                  <li>
                    Select the same Cloud Provider and Region in the dropdown
                    above.
                  </li>
                </ol>
                <div style={{ marginTop: "8px" }}>
                  <strong>
                    Note: Starter plan supports only AWS (us-east-1).
                  </strong>
                </div>
              </AccordionDetails>
            </Accordion>
            <Accordion sx={accordionStyles.accordionRoot}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon color="white" />}
                sx={accordionStyles.accordionSummary}
              >
                <Typography>
                  Don't want to add keys? (Limitations will apply)
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={accordionStyles.accordionDetails}>
                <ul>
                  <li>Create a single bot</li>
                  <li>Upload upto 5 files and 20Mbs total size </li>
                  <li>Enter upto 5 Ideal Responses</li>
                  <li>Run and evaluate upto 5 combinations</li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion sx={accordionStyles.accordionRoot}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon color="white" />}
                sx={accordionStyles.accordionSummary}
              >
                <Typography>With OpenAI and Pinecone Keys</Typography>
              </AccordionSummary>
              <AccordionDetails sx={accordionStyles.accordionDetails}>
                <ul>
                  <li>No limitations on usage/upload</li>
                  <li>Cannot Deploy the app on our cloud</li>
                  <li>
                    No limitation on file upload, and hyperparamter combinations
                  </li>
                </ul>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default MyKeys;
